import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const MediterraneanADUDesignSanBruno = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mediterranean ADU Design & Construction in San Bruno",
    "description": "Create elegant Mediterranean ADUs in San Bruno featuring stucco walls, tile roofs, and warm Old World charm with modern amenities.",
    "url": "https://www.udaadu.com/mediterranean-adu-design-san-bruno",
    "mainEntity": {
      "@type": "Service",
      "name": "Mediterranean ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "San Bruno, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/mediterranean-adu-design-san-bruno" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Mediterranean ADU Design & Construction in San Bruno
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create elegant Mediterranean ADUs in San Bruno featuring stucco walls, tile roofs, and warm Old World charm with modern amenities.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Mediterranean ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Why Choose Mediterranean ADUs in San Bruno?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Timeless Elegance</h3>
              <p className="text-muted-foreground">
                Mediterranean architecture brings Old World sophistication to San Bruno, creating ADUs with enduring appeal and classic beauty.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Climate Compatibility</h3>
              <p className="text-muted-foreground">
                Designed for warm climates, Mediterranean features like stucco and tile naturally suit San Bruno's moderate coastal weather.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Outdoor Living</h3>
              <p className="text-muted-foreground">
                Courtyards, patios, and outdoor spaces extend living areas and take advantage of San Bruno's pleasant year-round climate.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Mediterranean ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Stucco walls in warm earth tones</li>
                <li>• Red clay tile or barrel tile roofs</li>
                <li>• Arched doorways and windows</li>
                <li>• Wrought iron details and balconies</li>
                <li>• Outdoor courtyards and patios</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Tile floors and decorative ceramics</li>
                <li>• Exposed wooden beams and coffered ceilings</li>
                <li>• Arched passages and alcoves</li>
                <li>• Natural stone and brick accents</li>
                <li>• Warm color palettes and textures</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">San Bruno Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Design Compatibility</h3>
              <p className="text-muted-foreground">
                Mediterranean ADUs complement San Bruno's diverse architectural landscape while adding distinctive character to residential neighborhoods.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Material Durability</h3>
              <p className="text-muted-foreground">
                Stucco and tile construction provides excellent durability in San Bruno's climate while requiring minimal maintenance over time.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-foreground">$250,000 - $400,000</p>
            <p className="text-muted-foreground">
              Mediterranean ADUs feature premium materials like tile and stucco. Custom details and outdoor spaces enhance value and appeal.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-foreground">Start Your Mediterranean ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to bring Mediterranean elegance to your San Bruno property? Let's design your perfect Old World retreat.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-foreground">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/spanish-colonial-adu-design-san-bruno">Spanish Colonial ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/tuscan-adu-design-san-bruno">Tuscan ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/mission-style-adu-design-san-bruno">Mission Style ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default MediterraneanADUDesignSanBruno;